import { useNavigate } from "react-router-dom";
import { Package, Edit, Trash2, ArrowLeft, DollarSign, Tag, Layers, Image as ImageIcon, Clock, Ruler, Sparkles, Award, Waves } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import useCustomParams from "../../../hooks/params/useCustomParams";
import { useState } from "react";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import { alertSuccess, alertServerError } from "../../../helpers/alertError";
import { useProduct } from "../../../hooks/products/useProduct";

export default function ProductDetailsPage() {
  const navigate = useNavigate();
  const { id } = useCustomParams();
  const { product, loading, deleteProduct } = useProduct({ id: id! });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!product?.id) return;
    setIsDeleting(true);
    try {
      await deleteProduct(product.id);
      alertSuccess("Produit supprimé avec succès");
      navigate(-1);
    } catch (error: any) {
      alertServerError(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Détails du produit">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!product) {
    return (
      <PageLayout title="Détails du produit">
        <div className="text-center py-20 text-gray-500">Produit non trouvé</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Détails du produit"
      actions={
        <div className="flex gap-2">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            <ArrowLeft size={18} /> Retour
          </button>
          <button 
            onClick={() => navigate(`/admin/products/update?id=${id}`)} 
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Edit size={18} /> Modifier
          </button>
          <button 
            onClick={() => setShowDeleteModal(true)} 
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <Trash2 size={18} /> Supprimer
          </button>
        </div>
      }
    >
      {/* En-tête avec image */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center gap-4">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
          ) : (
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <Package size={40} className="text-primary" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{product.name}</h1>
            {product.badge && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                {product.badge}
              </span>
            )}
            <p className="text-sm text-gray-500 mt-1">slug: {product.slug}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Informations générales */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Layers size={18} className="text-primary" />
            Informations générales
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Nom</span>
              <span className="font-medium">{product.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Type</span>
              <span>{product.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Badge</span>
              <span>{product.badge || '-'}</span>
            </div>
          </div>
        </div>

        {/* Caractéristiques capillaires */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Sparkles size={18} className="text-primary" />
            Caractéristiques
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500 flex items-center gap-1">
                <Award size={16} className="text-gray-400" /> Qualité
              </span>
              <span className="font-medium">{product.quality}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 flex items-center gap-1">
                <Layers size={16} className="text-gray-400" /> Densité
              </span>
              <span>{product.density}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 flex items-center gap-1">
                <Waves size={16} className="text-gray-400" /> Texture
              </span>
              <span>{product.texture}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 flex items-center gap-1">
                <Ruler size={16} className="text-gray-400" /> Longueur par défaut
              </span>
              <span>{product.defaultLength}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 flex items-center gap-1">
                <Ruler size={16} className="text-gray-400" /> Longueurs disponibles
              </span>
              <span className="flex flex-wrap gap-1 justify-end">
                {product.lengths.map((length, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                    {length}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>

        {/* Prix */}
        <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign size={18} className="text-primary" />
            Prix
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Prix</span>
              <span className="font-bold text-lg text-primary">{product.priceLabel}</span>
            </div>
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Tag size={18} className="text-primary" />
              Description
            </h2>
            <p className="text-gray-600 leading-relaxed">{product.description}</p>
          </div>
        )}

        {/* Image */}
        {product.imageUrl && (
          <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ImageIcon size={18} className="text-primary" />
              Image du produit
            </h2>
            <div className="max-w-md">
              <img src={product.imageUrl} alt={product.name} className="w-full h-auto rounded-lg border" />
            </div>
          </div>
        )}

        {/* Métadonnées */}
        <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock size={18} className="text-primary" />
            Métadonnées
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">ID</span>
              <span className="font-mono">{product.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Slug</span>
              <span className="font-mono">{product.slug}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Créé le</span>
              <span>{new Date(product.createdAt).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Dernière modification</span>
              <span>{new Date(product.updatedAt).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
        onConfirm={handleDelete} 
        title="Supprimer le produit" 
        message={`Supprimer "${product.name}" ?`} 
        confirmText={isDeleting ? "Suppression..." : "Supprimer"} 
      />
    </PageLayout>
  );
}